import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, ChevronRight } from 'lucide-react';
import SiteMobileHeader from '../components/SiteMobileHeader';
import homeHeroLogo from '../assets/Group12.svg';
import MailingListModal from '../components/MailingListModal';
import StickyNavigation from '../components/StickyNavigation';
import HomeContactFooter from '../components/HomeContactFooter';
import NewsArticleCard from '../components/NewsArticleCard';
import {
  cardImage,
  cbnmeArticleImage,
  eiraBuildingImage,
  eiraCelebrationImage,
  emiratesProjectsArticleImage,
  etihadRailImage,
  gulfNewsIntegratedCommunitiesImage,
  ibtimesQuoteImage,
  khaleejTimesArticleImage,
  lofficielArabiaEiraImage,
  logisticsGulfArticleImage,
  pressNewsImage,
  pressReleaseHeroDesktop,
  pressReleaseHeroMobile,
} from '../cdn/pressMedia';
import notificationIcon from '../assets/shared/icons/notification-bell.svg';
import gulfNewsLogo from '../assets/shared/press/gulf-news-logo.png';
import alBayanLogo from '../assets/press-release/publishers/logos/al-bayan-logo.svg';
import alWatanLogo from '../assets/alwatan-removebg-preview.png';
import tradeArabiaLogo from '../assets/press-release/publishers/logos/tradearabia-logo-transparent.png';
import gulfConstructionLogo from '../assets/press-release/publishers/logos/gulf-construction-logo-transparent.png';
import zawyaLogo from '../assets/press-release/publishers/logos/zawya-logo.png';
import cbnmeLogo from '../assets/press-release/publishers/logos/cbnme-logo-transparent.png';
import almadarLogo from '../assets/press-release/publishers/logos/eira-sellout/almadar-transparent.png';
import dubaiMagazineLogo from '../assets/press-release/dubai_magazine.png';
// import gulfNewsBusinessLogo from '../assets/press-release/gulf_news.png';
import gulfNewsBusinessLogo from '../assets/press-release/publishers/logos/eira-sellout/gulfnewsbusiness.png';
import arabianBusinessWeekLogo from '../assets/press-release/arabian_business_week_2.png';
import menews247Logo from '../assets/press-release/publishers/logos/eira-sellout/menews247-transparent.png';
import asiaNewsTrendsLogo from '../assets/press-release/publishers/logos/eira-sellout/asianewstrends-transparent.png';
import uaeNews247Logo from '../assets/press-release/publishers/logos/eira-sellout/uaenews247-transparent.png';
import pantimeArabiaLogo from '../assets/press-release/publishers/logos/eira-sellout/pantimearabia-transparent.png';
import uaeNews4uLogo from '../assets/press-release/publishers/logos/eira-sellout/uaenews4u-transparent.png';
import dxbHappeningsLogo from '../assets/press-release/publishers/logos/eira-sellout/dxbhappenings-transparent.png';
import bizpreneurMeLogo from '../assets/press-release/publishers/logos/eira-sellout/bizpreneurme-transparent.png';
import lofficielArabiaLogo from '../assets/press-release/publishers/logos/eira-sellout/lofficiel.png';
import khaleejTimesLogo from '../assets/press-release/khaleejtimes.png';
import ObserverDubaiLogo from '../assets/press-release/observer_dubai.jpg'
import FashionWithStyleLogo from '../assets/press-release/fashion_with_style.jpg'
import EmiratesProjectLogo from '../assets/press-release/emirates_projects.png'
import LogisticsGulfLogo from '../assets/press-release/logistics_gulf.png'
import ibtimesLogo from '../assets/press-release/publishers/logos/ibtimes-logo.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import useStaggeredRiseOnView, { staggerLineClass } from '../hooks/useStaggeredRiseOnView';
import useReplayOnScrollReveal from '../hooks/useReplayOnScrollReveal';
import './LandingPage.css';
import '../styles/SitePageShell.css';
import './PressReleasePage.css';

const MAIL_MODAL_CLOSE_MS = 620;
const FEATURED_ARTICLE_URL =
  'https://www.khaleejtimes.com/uae/perfect-timing-myth-dubai-it-lives-built-businesses';
const PRESS_READ_MORE_CLASS =
  "group mt-7 inline-flex h-[30px] w-fit min-w-[113px] self-start items-center justify-center gap-[8px] rounded-full border border-[#bc8639] bg-transparent px-3 font-['Poppins',sans-serif] text-[10px] font-medium leading-[15.82px] tracking-[0] text-[#bc8639] no-underline transition-all duration-300 ease-out hover:-translate-y-[1px] hover:bg-[#bc8639] hover:text-white hover:shadow-[0_8px_18px_rgba(188,134,57,0.22)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#bc8639] active:translate-y-0 md:mt-8";

const PRESS_LOAD_MORE_CLASS =
  "group inline-flex h-[38px] min-w-[148px] items-center justify-center gap-[8px] rounded-full border border-[#bc8639] bg-transparent px-5 font-['Poppins',sans-serif] text-[11px] font-medium uppercase tracking-[0.12em] text-[#bc8639] transition-all duration-300 ease-out hover:-translate-y-[1px] hover:bg-[#bc8639] hover:text-white hover:shadow-[0_8px_18px_rgba(188,134,57,0.22)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#bc8639] active:translate-y-0";

const EIRA_SELL_OUT_TITLE =
  'Neoterra Developments celebrates the sell-out of Eira in Dubai Production City';
const EIRA_SELL_OUT_SUMMARY =
  'The landmark achievement reflects strong market trust in Neoterra’s vision and GRID’s disciplined execution, with an exclusive appreciation evening at Verde, Four Seasons Resort Dubai.';
const EIRA_SELL_OUT_IMAGE_ALT = 'Neoterra Developments celebrating the sell-out of Eira';

const FEATURED_PUBLISHER_NEWS_CARDS = [
  {
    id: 'gulf-news-integrated-communities',
    publisherName: 'Gulf News',
    publisherLogo: gulfNewsLogo,
    image: gulfNewsIntegratedCommunitiesImage,
    title:
      'The next wave of growth will be driven by demand for integrated communities: Omesh Kumar Gurnani',
    summary:
      'Neoterra Developments Co-Founder shares his thoughts on Dubai\'s real estate market and why integrated communities will define the next phase of growth.',
    imageAlt: 'Omesh Kumar Gurnani, Co-Founder of Neoterra Developments',
    imageFit: 'contain',
    url: 'https://gulfnews.com/gn-focus/the-next-wave-of-growth-will-be-driven-by-demand-for-integrated-communities-omesh-kumar-gurnani-1.500599473',
  },
  {
    id: 'khaleej-times-omesh',
    publisherName: 'Khaleej Times',
    publisherLogo: khaleejTimesLogo,
    image: khaleejTimesArticleImage,
    title: 'How Omesh Kumar Gurnani is shaping Neoterra Developments through long-term thinking',
    summary:
      'The founder shares how years of entrepreneurial experience continue to influence the company\'s approach to quality, trust and lasting value.',
    imageAlt: 'Omesh Kumar Gurnani, CEO and Founder of Neoterra Developments',
    imageFit: 'contain',
    url: 'https://www.khaleejtimes.com/business-technology-review/how-omesh-kumar-gurnani-is-shaping-neoterra-developments-through-long-term-thinking?_refresh=true',
  },
  {
    id: 'cbnme-omesh',
    publisherName: 'Construction Business News Middle East',
    publisherLogo: cbnmeLogo,
    image: cbnmeArticleImage,
    title:
      'Omesh Kumar Gurnani Says Dubai\'s Next Residential Chapter Will Be Defined By Communities, Not Just Buildings',
    summary:
      'Neoterra\'s CEO says buyers are placing greater emphasis on quality, transparency, community, and long-term investment value.',
    imageAlt: 'Omesh Kumar Gurnani, Co-Founder of Neoterra Developments',
    imageFit: 'contain',
    url: 'https://www.cbnme.com/expert-insight/omesh-kumar-gurnani-says-dubais-next-residential-chapter-will-be-defined-by-communities-not-just-buildings/',
  },
  {
    id: 'ibtimes-branded-residences',
    publisherName: 'International Business Times Singapore',
    publisherLogo: ibtimesLogo,
    image: ibtimesQuoteImage,
    title:
      'Luxury Real Estate\'s New Status Symbol: Why Branded Residences Are Outpacing Traditional Luxury Homes',
    summary:
      'Naresh Perwani, founder and chairman of Neoterra Developments, says a recognised brand provides familiarity and confidence for buyers making investment decisions from abroad.',
    imageAlt: 'Naresh Perwani quote on branded residences, told IBTimes Singapore',
    imageFit: 'contain',
    url: 'https://www.ibtimes.sg/luxury-real-estates-new-status-symbol-why-branded-residences-are-outpacing-traditional-luxury-89477',
  },
  {
    id: 'zawya',
    publisherName: 'Zawya',
    publisherLogo: zawyaLogo,
    image: pressNewsImage,
    title: 'Neoterra Developments marks groundbreaking of ELMORA at Jumeirah Garden City',
    summary:
      'Zawya highlights the project location just off Sheikh Zayed Road, minutes from DIFC and the AED100 billion DIFC 2.0 expansion.',
    imageFit: 'contain',
    url: 'https://www.zawya.com/en/press-release/companies-news/neoterra-developments-marks-groundbreaking-of-elmora-at-jumeirah-garden-city-kjijkywf',
  },
  {
    id: 'gulf-news-etihad-rail',
    publisherName: 'Gulf News',
    publisherLogo: gulfNewsLogo,
    image: etihadRailImage,
    title: 'Etihad Rail may narrow Dubai rent gap, but prices will not equalise, experts say renters will move first',
    summary:
      'Naresh Perwani, Founder and Chairman of NeoTerra Developments, says Etihad Rail should be viewed as a long-term enabler of growth, with thoughtfully planned communities key to lasting value.',
    imageAlt: 'Etihad Rail passenger station and train',
    imageFit: 'contain',
    url: 'https://gulfnews.com/business/markets/etihad-rail-may-narrow-dubai-rent-gap-but-prices-will-not-equalise-1.500599620',
  },
  {
    id: 'emirates-projects-prime-addresses',
    publisherName: 'Emirates Projects',
    // publisherLogoText: 'Emirates Projects',
    publisherLogo: EmiratesProjectLogo,
    image: emiratesProjectsArticleImage,
    title: 'Dubai’s Next Prime Addresses Are Emerging',
    summary:
      'Omesh Kumar Gurnani explains why evolving districts with strong infrastructure, connectivity, and future growth potential are shaping Dubai’s next prime addresses.',
    imageAlt: 'Dubai skyline for Emirates Projects feature on emerging prime addresses',
    imageFit: 'contain',
    url: 'https://emirates.tpg.media/dubais-next-prime-addresses-are-emerging/',
  },
  {
    id: 'logisticsgulf-boutique-oped',
    publisherName: 'LogisticsGulf',
    publisherLogo: LogisticsGulfLogo,
    // publisherLogoText: 'LogisticsGulf',
    image: logisticsGulfArticleImage,
    title: 'Why boutique will outperform mega-scale in Dubai’s next real estate cycle',
    summary:
      'In this op-ed, Omesh Kumar Gurnani shares why boutique developments can better deliver personalisation, quality, and long-term value in Dubai’s evolving property market.',
    imageAlt: 'Dubai skyline for LogisticsGulf op-ed on boutique real estate',
    imageFit: 'contain',
    url: 'https://logisticsgulf.com/2026/07/oped-contribution-why-boutique-will-outperform-mega-scale-in-dubais-next-real-estate-cycle/',
  },
  {
    id: 'zawya-eira-sell-out',
    publisherName: 'Zawya',
    publisherLogo: zawyaLogo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://www.zawya.com/en/press-release/companies-news/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city-417628',
  },
  {
    id: 'almadar-eira-sell-out',
    publisherName: 'AlMadar Magazine',
    publisherLogo: almadarLogo,
    image: eiraBuildingImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://www.almadarmagazine.ae/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'dubai-magazine-eira-sell-out',
    publisherName: 'Dubai Magazine',
    publisherLogo: dubaiMagazineLogo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://www.dubaimagazine.net/wp/?p=28659&lang=en',
  },
  {
    id: 'menews247-eira-sell-out',
    publisherName: 'Middle East News 247',
    publisherLogo: menews247Logo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://menews247.com/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'asianewstrends-eira-sell-out',
    publisherName: 'Asia News Trends',
    publisherLogo: asiaNewsTrendsLogo,
    image: eiraBuildingImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://asianewstrends.com/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'gulfnewsbusiness-eira-sell-out',
    publisherName: 'Gulf News Business',
    publisherLogo: gulfNewsBusinessLogo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://gulfnewsbusiness.com/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'uaenews247-eira-sell-out',
    publisherName: 'UAE News 247',
    publisherLogo: uaeNews247Logo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://uaenews247.com/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'fashionwithstyle-eira-sell-out',
    publisherName: 'The Fashion With Style',
    publisherLogo: FashionWithStyleLogo,
    // publisherLogoText: 'The Fashion With Style',
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://thefashionwithstyle.com/real-estate-retail/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'pantimearabia-eira-sell-out',
    publisherName: 'Pantime Arabia',
    publisherLogo: pantimeArabiaLogo,
    image: eiraBuildingImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://pantimearabia.com/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'uaenews4u-eira-sell-out',
    publisherName: 'UAE News 4U',
    publisherLogo: uaeNews4uLogo,
    image: eiraBuildingImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://uaenews4u.com/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'dxbhappenings-eira-sell-out',
    publisherName: 'DXB Happenings',
    publisherLogo: dxbHappeningsLogo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://dxbhappenings.net/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'arabianbusinessweek-eira-sell-out',
    publisherName: 'Arabian Business Week',
    publisherLogo: arabianBusinessWeekLogo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    imageFit: 'contain',
    url: 'https://arabianbusinessweek.net/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'arabdaily-eira-sell-out',
    publisherName: 'Arab Daily',
    publisherLogoText: 'Arab Daily',
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageFit: 'contain',
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    url: 'https://arabdaily.net/2026/07/31/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'observerdubai-eira-sell-out',
    publisherName: 'Observer Dubai',
    publisherLogo: ObserverDubaiLogo,
    // publisherLogoText: 'Observer Dubai',
    image: eiraBuildingImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageFit: 'contain',
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    url: 'https://observerdubai.com/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'bizpreneurme-eira-sell-out',
    publisherName: 'Bizpreneur ME',
    publisherLogo: bizpreneurMeLogo,
    image: eiraCelebrationImage,
    title: EIRA_SELL_OUT_TITLE,
    summary: EIRA_SELL_OUT_SUMMARY,
    imageFit: 'contain',
    imageAlt: EIRA_SELL_OUT_IMAGE_ALT,
    url: 'https://www.bizpreneurme.com/neoterra-developments-celebrates-the-sell-out-of-eira-in-dubai-production-city/',
  },
  {
    id: 'lofficiel-arabia-five-star-homes',
    publisherName: "L'Officiel Arabia",
    publisherLogo: lofficielArabiaLogo,
    image: lofficielArabiaEiraImage,
    imageFit: 'contain',
    title: "Why Dubai's Newest Homes Are Starting to Feel Like Five-Star Hotels",
    summary:
      'L’Officiel Arabia looks at how developments like Eira are borrowing the luxury hospitality playbook — curated amenities, lifestyle, and an experience worth coming home to.',
    imageAlt: 'Eira by Neoterra Developments featured in L’Officiel Arabia',
    url: 'https://lofficielarabia.com/format/WhyDubaisNewestHomesAreStartingtoFeelLikeFiveStarHotels.php?id=7613&table_name=master_table',
  },
];

const LEGACY_PUBLISHER_NEWS_CARDS = [
  {
    id: 'al-bayan',
    publisherName: 'Al Bayan',
    publisherLogo: alBayanLogo,
    image: pressNewsImage,
    title: '«نيـوتيرا» تضع حجر أساس «إلمورا» في جميرا جاردن سيتي بتكلفة 130 مليون درهم',
    summary:
      'أعلنت الشركة وضع حجر الأساس لمشروع «إلمورا» في جميرا جاردن سيتي، وكشفت عن مشروعها المقبل في مدينة دبي للإنتاج.',
    imageFit: 'contain',
    url: 'https://www.albayan.ae/economy/business/uae-economy/1166749',
    contentDir: 'rtl',
  },
  {
    id: 'al-watan',
    publisherName: 'Al Watan',
    publisherLogo: alWatanLogo,
    image: pressNewsImage,
    title: '“نيـوتيرا” تضع حجر أساس مشروع “إلمورا” في جميرا جاردن سيتي بتكلفة 130 مليون درهم',
    summary:
      'بدأت نيـوتيرا أعمال الإنشاء في مشروع «إلمورا»، مع موعد تسليم مستهدف في فبراير 2028 وخطة لإطلاق مشروع جديد في دبي.',
    imageFit: 'contain',
    url: 'https://alwatan.ae/posts/1605820',
    contentDir: 'rtl',
  },
  {
    id: 'tradearabia',
    publisherName: 'TradeArabia',
    publisherLogo: tradeArabiaLogo,
    image: pressNewsImage,
    title: 'Neoterra Developments breaks ground for Elmora at Jumeirah Garden City',
    summary:
      'The developer confirmed Elmora is scheduled for completion in February 2028 and announced its next Dubai Production City project.',
    imageFit: 'contain',
    url: 'https://www.tradearabia.com/News/413435/Neoterra-Developments-breaks-ground-for-Elmora-at-Jumeirah-Garden-City-',
  },
  {
    id: 'gulf-construction',
    publisherName: 'Gulf Construction',
    publisherLogo: gulfConstructionLogo,
    image: pressNewsImage,
    title: 'Neoterra Developments breaks ground for Elmora at Jumeirah Garden City',
    summary:
      'The report covers the AED130 million residential tower, GRID partnership, unit mix, and strong early sales momentum.',
    imageFit: 'contain',
    url: 'https://gulfconstructiononline.com/ArticleTA/413435',
  },
  {
    id: 'gulf-news-elmora',
    publisherName: 'Gulf News',
    publisherLogo: gulfNewsLogo,
    image: pressNewsImage,
    title: 'Neoterra Developments breaks ground for Elmora',
    summary:
      'Neoterra Developments, a Dubai-based real estate developer focused on creating premium residential communities, has commenced construction of ELMORA at Jumeirah Garden City.',
    imageAlt: 'ELMORA ground breaking ceremony',
    imageFit: 'contain',
    url: 'https://gulfnews.com/business/property/neoterra-developments-breaks-ground-for-elmora-unveils-next-project-in-dubai-production-city-1.500493211',
  },
];

const PUBLISHER_NEWS_CARDS = [...FEATURED_PUBLISHER_NEWS_CARDS, ...LEGACY_PUBLISHER_NEWS_CARDS];

const getColumnsPerRow = () => {
  if (typeof window === 'undefined') return 3;
  const width = window.innerWidth;
  if (width >= 1280) return 3;
  if (width >= 768) return 2;
  return 1;
};

const getInitialVisibleCount = (columnsPerRow) => {
  if (columnsPerRow === 1) return 4;
  return columnsPerRow * 2;
};

const DESKTOP_PUBLISHER_INITIAL_COUNT = 6;
const DESKTOP_PUBLISHER_GRID_COLUMNS = 3;
const DESKTOP_LOAD_MORE_ROW_COUNT = 2;
const DESKTOP_LOAD_MORE_BATCH = DESKTOP_PUBLISHER_GRID_COLUMNS * DESKTOP_LOAD_MORE_ROW_COUNT;

const chunkCardsByColumns = (cards, columns) => {
  const rows = [];
  for (let index = 0; index < cards.length; index += columns) {
    rows.push(cards.slice(index, index + columns));
  }
  return rows;
};

const PressReleasePage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [promotionalConsent, setPromotionalConsent] = useState(false);
  const [showAllCards, setShowAllCards] = useState(false);
  const [desktopExtraLoaded, setDesktopExtraLoaded] = useState(0);
  const [desktopRevealGeneration, setDesktopRevealGeneration] = useState(0);
  const [columnsPerRow, setColumnsPerRow] = useState(getColumnsPerRow);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 402));
  const pressHeroRevealRef = useRef(null);
  const pressPublishersRevealRef = useRef(null);

  const desktopInitialCards = useMemo(
    () => PUBLISHER_NEWS_CARDS.slice(0, DESKTOP_PUBLISHER_INITIAL_COUNT),
    []
  );

  const desktopExpandedCards = useMemo(
    () => PUBLISHER_NEWS_CARDS.slice(DESKTOP_PUBLISHER_INITIAL_COUNT),
    []
  );

  const isPressMobile = viewportWidth < 1024;

  const desktopExpandedVisibleRows = useMemo(
    () =>
      chunkCardsByColumns(
        desktopExpandedCards.slice(0, desktopExtraLoaded),
        DESKTOP_PUBLISHER_GRID_COLUMNS
      ),
    [desktopExpandedCards, desktopExtraLoaded]
  );

  const desktopExpandedVisibleCards = useMemo(
    () => desktopExpandedCards.slice(0, desktopExtraLoaded),
    [desktopExpandedCards, desktopExtraLoaded]
  );

  useReplayOnScrollReveal(
    !isPressMobile && desktopExtraLoaded > 0
      ? `.press-release-publishers-desktop__grid[data-reveal-gen="${desktopRevealGeneration}"] .press-release-publishers-desktop__expanded-row`
      : '.press-release-publishers-desktop__expanded-row--idle',
    'is-visible',
    0.12,
    '80px 0px -8% 0px',
    false
  );

  useReplayOnScrollReveal(
    isPressMobile
      ? `.press-release-publishers-desktop__grid[data-reveal-gen="${desktopRevealGeneration}"] .press-release-publishers-desktop__grid-item`
      : '.press-release-publishers-desktop__grid-item--idle-reveal',
    'is-visible',
    0.14,
    '0px 0px -6% 0px',
    false
  );

  useReplayOnScrollReveal(
    showAllCards ? '#press-release-publishers .press-release-card--reveal' : '.press-release-card--idle',
    'is-inview',
    0.1,
    '80px 0px -8% 0px',
    false
  );

  useStaggeredRiseOnView(pressHeroRevealRef, {
    individual: true,
    durationMs: 1800,
    distancePx: 28,
    threshold: 0.01,
    rootMargin: '0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  useStaggeredRiseOnView(pressPublishersRevealRef, {
    individual: true,
    observeMutations: false,
    durationMs: 2000,
    distancePx: 40,
    threshold: 0.12,
    rootMargin: '0px 0px 0px 0px',
    replay: true,
    reverseOnLeave: true,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  useEffect(() => {
    const onResize = () => setColumnsPerRow(getColumnsPerRow());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const initialVisibleCount = useMemo(
    () => getInitialVisibleCount(columnsPerRow),
    [columnsPerRow]
  );

  const visibleCards = useMemo(() => {
    if (showAllCards) return PUBLISHER_NEWS_CARDS;
    return PUBLISHER_NEWS_CARDS.slice(0, initialVisibleCount);
  }, [showAllCards, initialVisibleCount]);

  const hasMoreDesktopCards = desktopExtraLoaded < desktopExpandedCards.length;

  const hasMoreCards = PUBLISHER_NEWS_CARDS.length > initialVisibleCount;

  const handleLoadMore = () => {
    setShowAllCards(true);
  };

  const handleDesktopLoadMore = () => {
    setDesktopExtraLoaded((prev) =>
      Math.min(prev + DESKTOP_LOAD_MORE_BATCH, desktopExpandedCards.length)
    );
    setDesktopRevealGeneration((generation) => generation + 1);
  };

  const scrollToContent = () => {
    const targetId = viewportWidth >= 1024 ? 'press-release-publishers-desktop' : 'press-release-news';
    const target = document.getElementById(targetId);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 46;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const openMailModal = () => {
    setMailClosing(false);
    setMailOpen(true);
  };

  const closeMailModal = () => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  };

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({ fullName, email, phone });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent,
          sourcePage: 'press',
        })
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setFullName('');
      setEmail('');
      setPhone('');
      setPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error');
    } finally {
      dispatch(clearContactState());
    }
  };

  return (
    <div className="press-release-page site-page-shell landing-page min-h-screen bg-[#fbfaf7]">
      <SiteMobileHeader
        variant="homeHero"
        logoSrc={homeHeroLogo}
        barClassName="home-top-bar press-release-home-top-bar"
        onEnquireClick={openMailModal}
      />

      <div className="press-release-main">
        <section className="press-release-hero-screen" aria-label="Press release hero">
          <figure className="press-release-hero-fig">
            <img
              src={pressReleaseHeroMobile}
              alt=""
              className="press-release-hero-img press-release-hero-img--mobile block lg:hidden"
              width={402}
              height={874}
              fetchPriority="high"
              decoding="async"
            />
            <img
              src={pressReleaseHeroDesktop}
              alt=""
              className="press-release-hero-img press-release-hero-img--desktop hidden lg:block"
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
            />
          </figure>
          <div className="press-release-hero-overlay hidden" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-[45%] z-[3] hidden flex-col items-center px-0 text-center">
            <h1 className="m-0 whitespace-nowrap text-center text-[32px] font-['Cinzel'] font-normal uppercase leading-[50.62px] tracking-[-1.27px] text-[#FFFFFF]">
              Press Release
            </h1>
            <p className="text-center text-[14px] font-['Poppins'] font-light leading-[100%] tracking-[0.42px] text-[#FFFFFF]">
              <span className="block whitespace-nowrap">Stories, features, and brand</span>
              <span className="mt-[4px] block">in the spotlight.</span>
            </p>
          </div>
          <div
            ref={pressHeroRevealRef}
            className="press-release-hero-copy press-release-hero-copy--desktop flex"
          >
            <h1 className="press-release-hero-desktop__title">
              <span
                className={`press-release-hero-desktop__title-line ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="0"
              >
                News,
              </span>
              <span
                className={`press-release-hero-desktop__title-line ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="150"
              >
                press &amp; insights
              </span>
            </h1>
            <p className="press-release-hero-desktop__lede">
              <span
                className={`press-release-hero-desktop__lede-line press-release-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="300"
              >
                Stay up to date with Neoterra
              </span>
              <span
                className={`press-release-hero-desktop__lede-line press-release-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="360"
              >
                Developments&apos; latest projects, community
              </span>
              <span
                className={`press-release-hero-desktop__lede-line press-release-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="420"
              >
                initiatives, press coverage, and perspectives
              </span>
              <span
                className={`press-release-hero-desktop__lede-line press-release-hero-desktop__lede-line--mobile ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="480"
              >
                shaping the future of thoughtful living.
              </span>
              <span
                className={`press-release-hero-desktop__lede-line press-release-hero-desktop__lede-line--desktop ${staggerLineClass}`}
                data-content-line
                data-reveal-delay="300"
              >
                Stay up to date with Neoterra Developments&apos; latest projects, community initiatives,
                press coverage, and perspectives shaping the future of thoughtful living.
              </span>
            </p>
          </div>

          <StickyNavigation
            wrapperClassName="home-navigation-sticky lg:hidden"
            onInquireClick={openMailModal}
          />

          <div className="hero-bottom-actions pointer-events-none absolute inset-x-0 bottom-0 z-[4] hidden w-full max-w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end justify-items-center lg:hidden">
            <button
              type="button"
              className="home-hero-mobile-mail relative z-[2] col-[1] justify-self-start ml-[clamp(6px,3vw,14px)] h-[38px] w-[38px] translate-x-1 -translate-y-6 cursor-pointer border-0 bg-transparent p-0 [pointer-events:auto] [-webkit-tap-highlight-color:transparent] [touch-action:manipulation] active:scale-[0.98] focus-visible:rounded-[8px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[rgba(255,255,255,0.9)] lg:col-start-3 lg:justify-self-end lg:ml-0 lg:mr-[clamp(22px,4vw,52px)] lg:translate-x-0"
              aria-label="Open mailing list form"
              onClick={openMailModal}
            >
              <span className="home-mail-icon" aria-hidden="true">
                <img src={notificationIcon} alt="" className="home-mail-icon__bell-img" />
              </span>
            </button>
            <button type="button" className="relative z-[2] col-[2] flex h-[42.2039909362793px] w-[25.314611434936523px] translate-y-2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 opacity-100 [pointer-events:auto] [-webkit-tap-highlight-color:transparent] [touch-action:manipulation]" aria-label="Scroll for more" onClick={scrollToContent}>
              <span className="box-border flex h-[42.2039909362793px] w-[25.314611434936523px] shrink-0 items-start justify-center rounded-[41785312px] border-[1.25px] border-solid border-[#c9a961] px-[8.25px] pt-[20.94px] [animation:homeMouseBounce_1500ms_ease-in-out_infinite] [transform:translate3d(0,0,0)] [will-change:transform] motion-reduce:animate-none" aria-hidden="true">
                <span className="h-[6.3307085037231445px] w-[6.3307085037231445px] shrink-0 rounded-[41785312px] bg-[#bc8639] [animation:homeMouseBounce_1500ms_ease-in-out_infinite] [transform:translate3d(0,0,0)] [will-change:transform] motion-reduce:animate-none" />
              </span>
            </button>
          </div>
        </section>

        <section
          id="press-release-news"
          className="relative z-[1] hidden w-full bg-[#fbfaf7] px-5 pb-14 pt-9 sm:px-7 md:px-[60px]"
          aria-label="Press release article"
        >
          <div className="w-full md:rounded-[14px] md:bg-white md:p-8 md:shadow-[0_4px_32px_rgba(0,0,0,0.06)]">
            <div className="mb-5 flex flex-wrap items-center gap-3 md:mb-6">
              <span className="inline-flex h-[28px] w-fit items-center justify-center rounded-full bg-[#bc8639] px-4 font-['Poppins'] text-[10px] font-semibold uppercase tracking-[0.18em] text-white md:h-[34px] md:px-5 md:text-[11px]">
                Featured
              </span>
              <img
                src={khaleejTimesLogo}
                alt="Khaleej Times"
                className="h-[18px] w-auto object-contain md:h-[22px]"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="grid w-full grid-cols-1 md:grid-cols-[2fr_3fr] md:items-stretch md:gap-x-8 lg:gap-x-10">
              <div className="flex min-w-0 flex-col md:col-start-1 md:row-start-1 md:row-span-2 md:min-h-0 md:justify-center md:self-stretch md:pr-2">
                <h2 className="m-0 font-['Cinzel'] text-[24px] font-light uppercase leading-[32.8px] tracking-[-1.17px] text-[#bc8639] md:text-[36px] md:leading-[1.2] lg:text-[32px] md:leading-[48px]">
                  <span className="md:hidden">
                    &apos;Perfect timing is a myth&apos;:
                    <br />
                    How Dubai-it attitude transformed lives
                  </span>
                  <span className="hidden md:inline">
                    &apos;Perfect timing is a myth&apos;: How Dubai-it attitude transformed lives, built businesses
                  </span>
                </h2>

                <div
                  className="mt-4 hidden h-0 w-24 shrink-0 border-0 border-t-2 border-solid border-t-[#bc8639] md:block"
                  role="presentation"
                  aria-hidden
                />

                <p className="mt-5 font-['Poppins'] text-[14px] font-light leading-[1.35] text-[rgba(10,10,10,0.76)] md:mt-5 md:text-[18px] md:leading-[1.5]">
                  Naresh Perwani, Founder and Chairman of Neoterra Developments, shares his Dubai-it
                  philosophy: dream big, work hard, move fast, and make sure whatever you do is done properly.
                </p>

                <a
                  href={FEATURED_ARTICLE_URL}
                  className={PRESS_READ_MORE_CLASS}
                  aria-label="Read the Khaleej Times feature on Neoterra's Dubai-it philosophy"
                >
                  Read more
                  <ChevronRight
                    size={14}
                    strokeWidth={1.5}
                    aria-hidden
                    className="transition-transform duration-300 ease-out group-hover:translate-x-[2px]"
                  />
                </a>
              </div>

              <img
                src={cardImage}
                alt="Naresh Perwani on Neoterra's Dubai-it philosophy"
                className="mt-7 block w-full rounded-[2px] object-cover md:col-start-2 md:row-start-1 md:row-span-2 md:mt-0 md:h-full md:min-h-[min(360px,52vh)] md:rounded-lg md:object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </section>

        <section
          id="press-release-publishers"
          className="hidden w-full bg-[#fbfaf7] px-5 pb-16 pt-2 sm:px-7 md:px-[60px] md:pb-20 md:pt-0"
          aria-label="Presence across leading publishers"
        >
          <h2 className="m-0 py-4 text-center font-['Cinzel'] text-[28px] font-light uppercase leading-[38px] tracking-[0.42px] text-[#bc8639] md:text-[36px] md:leading-[48px]">
            Presence across leading publishers
          </h2>
          <div className="mx-auto mt-8 w-full max-w-[1200px] md:mt-10">
            <div className="grid w-full grid-cols-1 items-stretch gap-[18px] md:grid-cols-2 md:gap-6 xl:grid-cols-3 xl:gap-8">
              {visibleCards.map((item, index) => {
                const shouldAnimate = showAllCards && index >= initialVisibleCount;
                const revealIndex = shouldAnimate ? index - initialVisibleCount : 0;

                return (
                  <div
                    key={item.id}
                    className={`h-full${shouldAnimate ? ' press-release-card--reveal' : ''}`}
                    style={
                      shouldAnimate
                        ? { transitionDelay: `${revealIndex * 90}ms` }
                        : undefined
                    }
                  >
                    <NewsArticleCard
                      className="shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
                      publisherLogoSrc={item.publisherLogo}
                      publisherLogoAlt={item.publisherName}
                      publisherLogoText={item.publisherLogoText}
                      imageSrc={item.image}
                      imageAlt={item.imageAlt ?? `${item.publisherName} coverage of the Elmora groundbreaking`}
                      title={item.title}
                      summary={item.summary}
                      readMoreHref={item.url}
                      readMoreAriaLabel={`Read ${item.publisherName} coverage`}
                      contentDir={item.contentDir}
                      imageFit={item.imageFit}
                      imageObjectPosition={item.imageObjectPosition}
                    />
                  </div>
                );
              })}
            </div>

            {hasMoreCards && !showAllCards ? (
              <div className="mt-10 flex justify-center md:mt-12">
                <button
                  type="button"
                  className={PRESS_LOAD_MORE_CLASS}
                  aria-label="Load more press coverage"
                  onClick={handleLoadMore}
                >
                  Load more
                  <ChevronDown
                    size={14}
                    strokeWidth={1.5}
                    aria-hidden
                    className="transition-transform duration-300 ease-out group-hover:translate-y-[2px]"
                  />
                </button>
              </div>
            ) : null}
          </div>
        </section>

        <section
          id="press-release-publishers-desktop"
          ref={pressPublishersRevealRef}
          className="press-release-publishers-desktop block"
          aria-label="Media presence across leading publishers"
        >
          <div className="press-release-publishers-desktop__inner">
            <header className="press-release-publishers-desktop__header">
              <p
                className={`press-release-publishers-desktop__kicker ${staggerLineClass}`}
                data-content-line
              >
                <span className="press-release-publishers-desktop__kicker-line" aria-hidden="true" />
                MEDIA
              </p>
              <h2 className="press-release-publishers-desktop__title">
                <span
                  className={`press-release-publishers-desktop__title-line press-release-publishers-desktop__title-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="150"
                >
                  Presence across
                </span>
                <span
                  className={`press-release-publishers-desktop__title-line press-release-publishers-desktop__title-line--mobile ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="300"
                >
                  leading publishers
                </span>
                <span
                  className={`press-release-publishers-desktop__title-line press-release-publishers-desktop__title-line--desktop ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="150"
                >
                  Presence across leading
                </span>
                <span
                  className={`press-release-publishers-desktop__title-line press-release-publishers-desktop__title-line--desktop ${staggerLineClass}`}
                  data-content-line
                  data-reveal-delay="300"
                >
                  publishers
                </span>
              </h2>
            </header>

            <div
              className="press-release-publishers-desktop__grid"
              data-reveal-gen={desktopRevealGeneration}
            >
              {desktopInitialCards.map((item) => (
                <div
                  key={item.id}
                  className={`press-release-publishers-desktop__grid-item${isPressMobile ? '' : ` ${staggerLineClass}`}`}
                  {...(isPressMobile ? {} : { 'data-content-line': '' })}
                >
                  <NewsArticleCard
                    layout="pressDesktop"
                    className={`press-release-publishers-desktop__card${item.image === eiraCelebrationImage ? ' press-release-desktop-card--eira-celebration' : ''
                      }`}
                    publisherLogoSrc={item.publisherLogo}
                    publisherLogoAlt={item.publisherName}
                    publisherLogoText={item.publisherLogoText}
                    imageSrc={item.image}
                    imageAlt={item.imageAlt ?? `${item.publisherName} coverage`}
                    title={item.title}
                    summary={item.summary}
                    readMoreHref={item.url}
                    readMoreLabel="Learn more"
                    readMoreAriaLabel={`Read ${item.publisherName} coverage`}
                    readMoreNewTab
                    contentDir={item.contentDir}
                    imageFit={item.imageFit}
                    imageObjectPosition={item.imageObjectPosition}
                  />
                </div>
              ))}

              {isPressMobile
                ? desktopExpandedVisibleCards.map((item) => (
                  <div
                    key={item.id}
                    className="press-release-publishers-desktop__grid-item press-release-publishers-desktop__grid-item--expanded"
                  >
                    <NewsArticleCard
                      layout="pressDesktop"
                      className={`press-release-publishers-desktop__card${item.image === eiraCelebrationImage ? ' press-release-desktop-card--eira-celebration' : ''
                        }`}
                      publisherLogoSrc={item.publisherLogo}
                      publisherLogoAlt={item.publisherName}
                      publisherLogoText={item.publisherLogoText}
                      imageSrc={item.image}
                      imageAlt={item.imageAlt ?? `${item.publisherName} coverage`}
                      title={item.title}
                      summary={item.summary}
                      readMoreHref={item.url}
                      readMoreLabel="Learn more"
                      readMoreAriaLabel={`Read ${item.publisherName} coverage`}
                      readMoreNewTab
                      contentDir={item.contentDir}
                      imageFit={item.imageFit}
                      imageObjectPosition={item.imageObjectPosition}
                    />
                  </div>
                ))
                : desktopExpandedVisibleRows.map((row, rowIndex) => (
                  <div
                    key={`expanded-row-${row[0]?.id ?? rowIndex}`}
                    className="press-release-publishers-desktop__expanded-row"
                  >
                    {row.map((item) => (
                      <div
                        key={item.id}
                        className="press-release-publishers-desktop__grid-item press-release-publishers-desktop__grid-item--expanded"
                      >
                        <NewsArticleCard
                          layout="pressDesktop"
                          className={`press-release-publishers-desktop__card${item.image === eiraCelebrationImage ? ' press-release-desktop-card--eira-celebration' : ''
                            }`}
                          publisherLogoSrc={item.publisherLogo}
                          publisherLogoAlt={item.publisherName}
                          publisherLogoText={item.publisherLogoText}
                          imageSrc={item.image}
                          imageAlt={item.imageAlt ?? `${item.publisherName} coverage`}
                          title={item.title}
                          summary={item.summary}
                          readMoreHref={item.url}
                          readMoreLabel="Learn more"
                          readMoreAriaLabel={`Read ${item.publisherName} coverage`}
                          readMoreNewTab
                          contentDir={item.contentDir}
                          imageFit={item.imageFit}
                          imageObjectPosition={item.imageObjectPosition}
                        />
                      </div>
                    ))}
                  </div>
                ))}

              {hasMoreDesktopCards ? (
                <div className="press-release-publishers-desktop__load-more-row">
                  <button
                    type="button"
                    className="press-release-publishers-desktop__load-more"
                    aria-label="Load more press coverage"
                    onClick={handleDesktopLoadMore}
                  >
                    Load More
                    <i aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <div className="relative z-[20] mt-2 lg:hidden">
          <HomeContactFooter sectionId="press-release-contact" />
        </div>
      </div>

      <div className="site-page-footer-wrap relative z-[10] isolate hidden bg-[#162D24] lg:block">
        <HomeContactFooter variant="home" sectionId="press-release-contact" />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        submitting={contactSubmitting}
        fullName={fullName}
        setFullName={setFullName}
        email={email}
        setEmail={setEmail}
        phone={phone}
        setPhone={setPhone}
        promotionalConsent={promotionalConsent}
        setPromotionalConsent={setPromotionalConsent}
        idPrefix="press-mail"
      />
    </div>
  );
};

export default PressReleasePage;
